// Shared note handling for every slideover with a History / Activity tab.
//
// Spread into the component: `...notesMixin`. It owns the links the editor
// attaches to the next note (`noteLinks`, passed to the editor by reference),
// posts notes to the one NotesController, and deletes or unlinks them. Each
// slideover keeps its own timeline array, so it implements
// `removeTimelineEntry(id)` to drop an entry from it.

const headers = () => ({
  'Accept': 'application/json',
  'Content-Type': 'application/json',
  'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
})

export const UNIFIED_NOTE_PREFIX = 'nt_'

export function isUnifiedNoteId(id) {
  return typeof id === 'string' && id.startsWith(UNIFIED_NOTE_PREFIX)
}

export function unifiedNoteId(id) {
  return String(id).replace(UNIFIED_NOTE_PREFIX, '')
}

// Create a note written on `subject` with the links picked in the editor.
// Resolves with the timeline entry, or throws with the server's message.
export async function postNote({ subject, content, links = [], occurredAt = null }) {
  const response = await fetch('/notes', {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({
      subject_type: subject.type,
      subject_id: subject.id,
      note: { content, occurred_at: occurredAt },
      links: links.map((l) => ({ type: l.type, id: l.id }))
    })
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.errors?.[0] || 'Failed to add note')
  }
  return response.json()
}

export async function destroyNote(entryId) {
  const response = await fetch(`/notes/${unifiedNoteId(entryId)}`, { method: 'DELETE', headers: headers() })
  if (!response.ok) throw new Error('Failed to delete note')
}

export async function unlinkNoteFrom(entryId, viewedFrom) {
  const params = new URLSearchParams({ notable_type: viewedFrom.type, notable_id: viewedFrom.id })
  const response = await fetch(`/notes/${unifiedNoteId(entryId)}/unlink?${params}`, { method: 'DELETE', headers: headers() })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.errors?.[0] || 'Failed to remove note from here')
  }
}

export const notesMixin = {
  // Links the editor attaches to the note being written (mutated in place)
  noteLinks: [],

  // Save the editor's content as a note on `subject`, with the picked links,
  // and hand back the timeline entry. Clears the links on success.
  async saveNote(subject, content) {
    const entry = await postNote({ subject, content: content.trim(), links: this.noteLinks })
    this.noteLinks.splice(0, this.noteLinks.length)
    return entry
  },

  // Take a note off this record's history without deleting it
  async unlinkNote(entry) {
    if (!entry?.unlinkable || !entry.viewed_from) return
    try {
      await unlinkNoteFrom(entry.id, entry.viewed_from)
      this.removeTimelineEntry(entry.id)
    } catch (error) {
      console.error('Failed to unlink note:', error)
      alert(error.message || 'Failed to remove note from here. Please try again.')
    }
  }
}
